/**
 * País y estilo de la matrícula del camión (oct 2026).
 *
 * La placa de la lista imita la matrícula real: banda azul de la UE con
 * estrellas, placa Mercosur argentina, placa negra argentina antigua… Se
 * decide al PINTAR, con dos pistas, así que vale igual para las cargas que ya
 * existen que para las nuevas:
 *
 *  1. El FORMATO del número, cuando es inequívoco. Dice de dónde es el camión,
 *     que es lo que cuenta la placa: un camión español cargando en Italia
 *     lleva matrícula española.
 *  2. El PAÍS DE LA CARGA (el que resuelve el servidor: puerto de salida en el
 *     flujo clásico, país de la planning en self-managed), para lo que el
 *     formato no resuelve solo.
 *
 * Por qué no la dirección de la fábrica, como en la app hermana: aquí casi
 * nunca nombra el país ("RIO SEGUNDO", "Gefyra - Thessaloniki", "VIA
 * FLAMINIA NORD, 35C - CAGLI (PU)") y en miles de cargas viene vacía. Leída
 * como texto habría puesto la "E" a Argentina, Grecia e Italia.
 *
 * Contrastado con las 176.000 matrículas del flujo clásico de producción
 * (5 oct 2026). Lo que obligó a usar el país de la carga:
 *  - Argentina usa desde 2016 el formato Mercosur `AB 123 CD`, IDÉNTICO al
 *    italiano. 563 matrículas argentinas salían con la "I" de Italia.
 *  - Las 634 `ABC123` que no se sabía de dónde eran son argentinas de antes de
 *    2016, y las 152 `ABCD12` son camiones chilenos que cargan en Argentina.
 *    Las dos formas existen en medio mundo; solo se atribuyen en Sudamérica.
 *
 * Cuando nada encaja —relleno como "X" o "ZZZ0000", erratas, un camión de un
 * país cuyo formato no se distingue— la placa sale genérica, sin país: mejor
 * eso que una bandera equivocada.
 */

export type PlateStyle =
  /** UE: banda azul con estrellas y código. Italia lleva también banda a la derecha. */
  | { kind: 'eu'; iso: string; code: string; yellow: boolean; rightBand: boolean }
  /** Fuera de la UE con banda azul propia (Reino Unido, Turquía…), sin estrellas. */
  | { kind: 'band'; iso: string; code: string }
  /** Argentina desde 2016: placa blanca con franja azul superior y el nombre del país. */
  | { kind: 'mercosur'; iso: string; caption: string }
  /** Argentina 1995-2016: placa negra con caracteres blancos. */
  | { kind: 'black'; iso: string; caption: string }
  /** Chile: placa blanca con el nombre del país en pequeño. */
  | { kind: 'caption'; iso: string; caption: string }
  /** Sin estilo propio. `iso` se conserva para el nombre accesible cuando se sabe. */
  | { kind: 'generic'; iso: string | null };

type PlateFormat = 'es' | 'it' | 'ar-mercosur' | 'ar-old' | 'cl';

const FORMAT_COUNTRY: Record<PlateFormat, string> = {
  es: 'es', it: 'it', 'ar-mercosur': 'ar', 'ar-old': 'ar', cl: 'cl',
};

/** Códigos provinciales de las matrículas españolas anteriores a 2000. */
const ES_PROVINCES = [
  // Dos letras primero: así la alternancia prueba "GI" antes que "G".
  'AB', 'AL', 'AV', 'BA', 'BI', 'BU', 'CA', 'CC', 'CE', 'CO', 'CR', 'CS', 'CU',
  'GC', 'GE', 'GI', 'GR', 'GU', 'HU', 'LE', 'LO', 'LU', 'MA', 'ML', 'MU', 'NA',
  'OR', 'OU', 'PM', 'PO', 'SA', 'SE', 'SG', 'SO', 'SS', 'TE', 'TF', 'TO', 'VA',
  'VI', 'ZA',
  'A', 'B', 'C', 'H', 'J', 'L', 'M', 'O', 'P', 'S', 'T', 'V', 'Z',
];

// Se distingue por la ESTRUCTURA (dónde caen letras y dígitos), no validando
// que la matrícula exista. Restringir los alfabetos hacía daño: excluir
// I/O/Q/U en Italia —lo que dice la norma de 1994— dejaba fuera 62 matrículas
// italianas auténticas (AE849VI, AA218JI...), y exigir consonantes en España
// descartaba 42 más por simples erratas.

// España desde septiembre de 2000: cuatro dígitos y tres letras.
const ES_MODERN = /^\d{4}[A-Z]{3}$/;

// Remolques y semirremolques españoles: igual, precedido de R.
const ES_TRAILER = /^R\d{4}[A-Z]{3}$/;

// España 1971-2000: provincia + cuatro dígitos + hasta dos letras. Aquí sí se
// valida la provincia: sin esa lista, cualquier cosa con dos letras y cuatro
// dígitos entraría como española.
const ES_OLD = new RegExp(`^(?:${ES_PROVINCES.join('|')})\\d{4}[A-Z]{0,2}$`);

// Remolques españoles anteriores a 2000: provincia + cinco dígitos + R
// ("B-28258-R"). Son 18.700 de los remolques de producción.
const ES_OLD_TRAILER = new RegExp(`^(?:${ES_PROVINCES.join('|')})\\d{5}R$`);

// Bulgaria usa el mismo esquema que la española antigua —sigla de provincia,
// cuatro dígitos y dos letras— y hasta las mismas siglas: "A 1234 PB" es
// Burgas, no Alicante. En una carga búlgara se lee como búlgara (los 15
// camiones de las cargas búlgaras de producción salían con la "E").
const ES_OLD_LOOKALIKE = new Set(['bg']);

// Dos letras, tres dígitos y dos letras: Italia desde 1994 y Argentina desde
// 2016 (Mercosur). Los TRES dígitos centrales la separan de la española
// antigua, que lleva cuatro; entre Italia y Argentina decide el país de carga.
const LLNNNLL = /^[A-Z]{2}\d{3}[A-Z]{2}$/;

// Remolques italianos: dos letras y cinco dígitos ("AD 93275"). En España la
// misma forma aparece como errata del remolque antiguo sin la R ("MU05341"),
// así que solo se atribuye en cargas italianas.
const IT_TRAILER = /^[A-Z]{2}\d{5}$/;

// Argentina 1995-2016: tres letras y tres dígitos.
const LLLNNN = /^[A-Z]{3}\d{3}$/;

// Chile desde 2007: cuatro letras y dos dígitos.
const LLLLNN = /^[A-Z]{4}\d{2}$/;

// Países sudamericanos donde carga Aldahra (puertos de salida reales: AR, CL,
// PY) y vecinos por los que pasan los camiones.
const SOUTH_AMERICA = new Set(['ar', 'cl', 'py', 'uy', 'bo', 'br']);

// Países cuyos formatos sí se reconocen. Si la carga es de uno de ellos y el
// número no encaja, la matrícula NO se le atribuye: suele ser relleno ("X",
// "ZZZ0000") o un camión de otro país, y la bandera de la carga mentiría.
const KNOWN_FORMAT_COUNTRIES = new Set(['es', 'it', ...SOUTH_AMERICA]);

// Código internacional de matrícula (Convención de Viena) de los 27 de la UE.
const EU_PLATE_CODE: Record<string, string> = {
  at: 'A', be: 'B', bg: 'BG', hr: 'HR', cy: 'CY', cz: 'CZ', dk: 'DK',
  ee: 'EST', fi: 'FIN', fr: 'F', de: 'D', gr: 'GR', hu: 'H', ie: 'IRL',
  it: 'I', lv: 'LV', lt: 'LT', lu: 'L', mt: 'M', nl: 'NL', pl: 'PL',
  pt: 'P', ro: 'RO', sk: 'SK', si: 'SLO', es: 'E', se: 'S',
};

// Placa de fondo amarillo.
const YELLOW_PLATE = new Set(['nl', 'lu']);

// Fuera de la UE, países cuya placa lleva banda azul con el código.
const BAND_PLATE_CODE: Record<string, string> = {
  gb: 'UK', tr: 'TR', ua: 'UA', md: 'MD', no: 'N', rs: 'SRB',
  al: 'AL', mk: 'NMK', me: 'MNE',
};

// Nombre del país tal y como va impreso en la placa.
const PRINTED_NAME: Record<string, string> = {
  ar: 'ARGENTINA', cl: 'CHILE',
};

/**
 * Deja el texto tal y como se compara: sin espacios, guiones ni puntos, y en
 * mayúsculas. Sin esto, "7814-hzx" no se reconocería.
 */
export function normalizePlate(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Formato reconocido del número, con el país de la carga para desempatar. */
function detectFormat(plate: string, load: string): PlateFormat | null {
  if (ES_MODERN.test(plate) || ES_TRAILER.test(plate) || ES_OLD_TRAILER.test(plate)) return 'es';
  if (ES_OLD.test(plate) && !ES_OLD_LOOKALIKE.has(load)) return 'es';
  const southAmerica = SOUTH_AMERICA.has(load);
  if (LLNNNLL.test(plate)) return southAmerica ? 'ar-mercosur' : 'it';
  if (load === 'it' && IT_TRAILER.test(plate)) return 'it';
  if (southAmerica && LLLNNN.test(plate)) return 'ar-old';
  if (southAmerica && LLLLNN.test(plate)) return 'cl';
  return null;
}

/**
 * ¿Parece una matrícula de verdad? Letras y dígitos, al menos cinco
 * caracteres y no un relleno de caracteres repetidos ("ZZZ0000", "XXX111").
 */
function looksLikePlate(plate: string): boolean {
  return plate.length >= 5
    && /[A-Z]/.test(plate)
    && /\d/.test(plate)
    && new Set(plate).size > 2;
}

/**
 * País de la matrícula (ISO-2 en minúsculas) y su formato, o null si no se
 * puede atribuir con seguridad.
 *
 * @param loadCountry ISO-2 del país de la carga (`resolveCountryIso`), si se sabe.
 */
export function detectPlateCountry(
  value: string | null | undefined,
  loadCountry?: string | null,
): { iso: string; format: PlateFormat | null } | null {
  const plate = normalizePlate(value);
  if (!plate) return null;
  const load = (loadCountry ?? '').trim().toLowerCase();

  const format = detectFormat(plate, load);
  if (format) return { iso: FORMAT_COUNTRY[format], format };

  // Formato no reconocido: se asume el país de la carga solo si es uno cuyos
  // formatos no se validan (Grecia, Bulgaria, Rumanía, Portugal…) y el número
  // tiene pinta de matrícula. "XX" o "ZZZ0000" se quedan sin país.
  if (load && load !== 'xx' && !KNOWN_FORMAT_COUNTRIES.has(load) && looksLikePlate(plate)) {
    return { iso: load, format: null };
  }
  return null;
}

/** Estilo de la placa para un número y el país de la carga. */
export function plateStyle(
  value: string | null | undefined,
  loadCountry?: string | null,
): PlateStyle {
  const found = detectPlateCountry(value, loadCountry);
  if (!found) return { kind: 'generic', iso: null };
  const { iso, format } = found;

  if (format === 'ar-mercosur') return { kind: 'mercosur', iso, caption: PRINTED_NAME.ar };
  if (format === 'ar-old') return { kind: 'black', iso, caption: PRINTED_NAME.ar };
  if (format === 'cl') return { kind: 'caption', iso, caption: PRINTED_NAME.cl };

  if (EU_PLATE_CODE[iso]) {
    return { kind: 'eu', iso, code: EU_PLATE_CODE[iso], yellow: YELLOW_PLATE.has(iso), rightBand: iso === 'it' };
  }
  if (BAND_PLATE_CODE[iso]) return { kind: 'band', iso, code: BAND_PLATE_CODE[iso] };
  return { kind: 'generic', iso };
}
